/**
 * Phase 11 slice 11C + HR-P2.3 — Drawing-page Export menu items.
 *
 * Pulled out of ``DrawingPage.tsx`` into this logic-only module
 * so the unit tests can exercise the items without spinning up a
 * DOM (this codebase doesn't carry @testing-library/react — see
 * the slice 4H journal note for context). The dropdown component
 * itself reads the items from this helper and renders each row.
 *
 * Four items, fixed order:
 *
 *   1. ``pdf``         — USPTO-compliant PDF (download).
 *                         Disabled when ``frozenCount === 0``
 *                         (per brief: "freeze at least one figure
 *                         to enable USPTO PDF export").
 *   2. ``pdf-preview`` — HR-P2.3 inline preview of the same PDF
 *                         (browser shows it in a new tab via
 *                         ``Content-Disposition: inline``). Same
 *                         disabled-state rule as ``pdf``.
 *   3. ``docx``        — Provenance DOCX (download). Always
 *                         enabled.
 *   4. ``zip``         — All deliverables ZIP (download). Always
 *                         enabled.
 */
import {
  drawingExportPdfUrl,
  drawingExportProvenanceDocxUrl,
  drawingExportZipUrl,
  drawingPreviewPdfUrl,
} from '@/api/client';

export type DrawingExportMenuKey = 'pdf' | 'pdf-preview' | 'docx' | 'zip';

export interface DrawingExportMenuItem {
  key: DrawingExportMenuKey;
  label: string;
  href: string;
  /** Suggested filename. The browser already gets one via the
   *  endpoint's Content-Disposition header; we surface it here
   *  so tests can assert on the suggested name without firing
   *  a real download. */
  filename: string;
  disabled: boolean;
  /** Tooltip text shown on hover, especially when ``disabled``
   *  to explain WHY (the brief asks for an explanatory tooltip
   *  on the disabled PDF item). */
  tooltip?: string;
  /** HR-P2.3 — controls the anchor's ``target`` attribute.
   *  ``"download"`` items keep the default (same tab; the
   *  browser's Content-Disposition: attachment intercepts
   *  navigation). ``"preview"`` items set ``target="_blank"``
   *  so the inline-disposition PDF opens in a new tab.
   *  ``rel="noopener noreferrer"`` is set in the consumer
   *  alongside this to prevent the new-tab from accessing
   *  ``window.opener``. */
  openMode: 'download' | 'preview';
}

export interface BuildExportMenuArgs {
  matterId: string;
  frozenCount: number;
}

export function buildDrawingExportMenuItems(
  { matterId, frozenCount }: BuildExportMenuArgs,
): DrawingExportMenuItem[] {
  const todayCompact = new Date().toISOString().slice(0, 10).replaceAll('-', '');
  const matterStub = matterId.slice(0, 8) || 'matter';
  // Fallback filenames the UI can show if the user hovers the
  // menu item. The actual filename on the downloaded file is
  // whatever the backend's Content-Disposition says — these are
  // best-effort presentations.
  const pdfFilename = `drawings_${matterStub}_${todayCompact}.pdf`;
  const docxFilename =
    `drawings_provenance_${matterStub}_${todayCompact}.docx`;
  const zipFilename = `drawings_${matterStub}_${todayCompact}.zip`;

  const pdfDisabled = frozenCount <= 0;

  // HR-P2.3 — preview filename mirrors the export filename with
  // a ``preview_`` prefix, matching the backend's
  // ``Content-Disposition: inline; filename="preview_*.pdf"``.
  const previewFilename = `preview_${matterStub}_${todayCompact}.pdf`;

  return [
    {
      key: 'pdf',
      label: 'Drawings PDF (USPTO)',
      href: drawingExportPdfUrl(matterId),
      filename: pdfFilename,
      disabled: pdfDisabled,
      tooltip: pdfDisabled
        ? 'Freeze at least one figure to enable USPTO PDF export.'
        : 'Black-and-white PDF that meets 37 CFR 1.84.',
      openMode: 'download',
    },
    {
      key: 'pdf-preview',
      label: 'Preview PDF',
      href: drawingPreviewPdfUrl(matterId),
      filename: previewFilename,
      disabled: pdfDisabled,
      tooltip: pdfDisabled
        ? 'Freeze at least one figure to enable USPTO PDF preview.'
        : 'Open the USPTO PDF in a new tab without downloading.',
      openMode: 'preview',
    },
    {
      key: 'docx',
      label: 'Provenance DOCX',
      href: drawingExportProvenanceDocxUrl(matterId),
      filename: docxFilename,
      disabled: false,
      tooltip:
        'Audit trail: figure inventory, numerals, claim edits, ' +
        'drafter’s notes (verbatim).',
      openMode: 'download',
    },
    {
      key: 'zip',
      label: 'All deliverables (ZIP)',
      href: drawingExportZipUrl(matterId),
      filename: zipFilename,
      disabled: false,
      tooltip:
        'Raw assets and metadata (figures, numerals, term ' +
        'mappings, edit log).',
      openMode: 'download',
    },
  ];
}
